/**
 * Terminal Format Utilities
 * Converts model data to/from terminal text format
 */

import { useModelStore } from "@/store/useModelStore";
import { useSupportStore } from "@/store/useSupportStore";
import { usePropertyStore } from "@/store/usePropertyStore";
import { useBeamTrussStore } from "@/hooks/useBeamTrussManagement";
import { useTrussStore } from "@/hooks/useTrussManagement";
import { useLoadcaseGroupStore } from "@/store/useLoadcaseGroupStore";
import { useBeamSectionStore } from "@/hooks/useBeamSection";
import { getDesignationDataForType } from "@/data/sectionDesignations";
import type { SectionType, MaterialType } from "@/types/property";
import type { BeamTrussType } from "@/types/beamTruss";

// ==================== EXPORT TO TERMINAL FORMAT ====================

const SUPPORT_POSITION_AXIS: Record<string, string> = {
  below: "-Z",
  above: "+Z",
  left: "-X",
  right: "+X",
  front: "+Y",
  back: "-Y",
};

const AXIS_TO_SUPPORT_POSITION: Record<string, string> = {
  "-Z": "below",
  "+Z": "above",
  "-X": "left",
  "+X": "right",
  "+Y": "front",
  "-Y": "back",
};

const VALID_SUPPORT_TYPES = ["pinned", "roller", "fixed"];
const VALID_BEAM_LOADCASE_TYPES = [
  "DISTRIBUTED1",
  "DISTRIBUTED2",
  "CONCENTRATED",
  "TWISTED",
];
const VALID_NODE_LOADCASE_KEYS = [
  "RX_X",
  "RX_Y",
  "RX_Z",
  "FX_X",
  "FX_Y",
  "FX_Z",
];

const SECTION_TYPES: SectionType[] = [
  "Bulb Angle",
  "Channel",
  "Custom Section",
  "Equal Angle",
  "I-Beam",
  "Tee Bar",
  "Unequal Angle",
];

// ==================== HELPER: Generate Random Color ====================
function generateRandomColor(): string {
  const colors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#FFA07A",
    "#98D8C8",
    "#F7DC6F",
    "#BB8FCE",
    "#85C1E2",
    "#F8B739",
    "#52C77E",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

// Format helpers
const formatBeamId = (id: string | number) => Number(id).toString();
const formatBeamNode = (nodeId: string | number) =>
  Number(nodeId).toString().padStart(3);
const formatBeam = (b: any) =>
  `${formatBeamId(b.id)}${formatBeamNode(b.startNodeId)}${formatBeamNode(b.endNodeId)};`;

const getSupportNodeIds = (s: any): string[] => {
  if (Array.isArray(s.nodeIds)) return s.nodeIds.map(String);
  if (Array.isArray(s.nodes)) return s.nodes.map(String);
  if (s.nodeId != null) return [String(s.nodeId)];
  return [];
};

const formatSupportNode = (s: any) => {
  const ids = getSupportNodeIds(s);
  if (ids.length === 0) return "";
  return ids.join(",");
};

const formatSupport = (s: any, idx: number) => {
  const ids = formatSupportNode(s);
  const axis = SUPPORT_POSITION_AXIS[s.position] ?? s.position;
  const base = `${idx + 1} ${axis} ${s.type?.toUpperCase()}`;

  if (s.type === "fixed" && s.dof) {
    const { Fx, Fy, Fz, Rx, Ry, Rz } = s.dof;
    return ids
      ? `${base} DOF(${Fx},${Fy},${Fz},${Rx},${Ry},${Rz}) ${ids};`
      : `${base} DOF(${Fx},${Fy},${Fz},${Rx},${Ry},${Rz}) ;`;
  }

  return ids ? `${base} ${ids};` : `${base} ;`;
};

const formatProperty = (p: any) => {
  const pid = (p.P_ID || "").toString();
  const material = (p.material || "None").padEnd(6);
  const sectionType = p.sectionType || "";
  const designation = p.sectionProperties?.designation
    ? ` ${p.sectionProperties.designation}`
    : "";
  const beamIds = p.beamIds?.length ? ` ${p.beamIds.join(",")}` : "";

  return `${pid} ${material} ${sectionType}${designation}${beamIds};`;
};

const formatNodesInRows = (nodes: any[]) => {
  const lines: string[] = [];
  let row: string[] = [];

  nodes.forEach((n, idx) => {
    row.push(`${n.id} ${n.x.toFixed(2)} ${n.y.toFixed(2)} ${n.z.toFixed(2)};`);

    if (row.length === 3 || idx === nodes.length - 1) {
      lines.push(row.join(" "));
      row = [];
    }
  });

  return lines.join("\n");
};

const formatBeamsInRows = (beams: any[]) => {
  const lines: string[] = [];
  let row: string[] = [];

  beams.forEach((b, idx) => {
    row.push(formatBeam(b));

    if (row.length === 4 || idx === beams.length - 1) {
      lines.push(row.join(" "));
      row = [];
    }
  });

  return lines.join("\n");
};

const formatSupportsInRows = (supports: any[]) => {
  const lines: string[] = [];
  let row: string[] = [];

  supports.forEach((s, idx) => {
    row.push(formatSupport(s, idx));

    if (row.length === 2 || idx === supports.length - 1) {
      lines.push(row.join(" "));
      row = [];
    }
  });

  return lines.join("\n");
};

const formatPropertiesInRows = (props: any[]) => {
  const lines: string[] = [];
  props.forEach((p) => {
    lines.push(formatProperty(p));
  });
  return lines.join("\n");
};

const groupNodeTrusses = (nodeTrussesArray: any[]) => {
  const grouped = new Map<string, any>();

  nodeTrussesArray.forEach((t) => {
    const key = t.presetName ?? t.label;

    if (!grouped.has(key)) {
      grouped.set(key, {
        id: key,
        name: key,
        type: t.type,
        nodeIds: [...t.nodeIds],
        rotationValues: { X: 0, Y: 0, Z: 0 },
        perpendicularValues: { X: 0, Y: 0, Z: 0 },
        visible: t.visible,
      });
    }

    const g = grouped.get(key);

    if (t.type === "regular") {
      g.rotationValues[t.plane] = t.rotationValue ?? 0;
    } else {
      g.perpendicularValues[t.plane] = t.rotationValue ?? 0;
    }

    t.nodeIds.forEach((id: string) => {
      if (!g.nodeIds.includes(id)) g.nodeIds.push(id);
    });
  });

  return Array.from(grouped.values());
};

const formatLoadcaseGroups = (
  loadcaseGroups: any[],
  beamTrusses: any[],
  groupedNodeTrusses: any[]
) => {
  const lines: string[] = [];

  loadcaseGroups.forEach((group) => {
    const groupBeamLoads = beamTrusses.filter((bt) =>
      group.beamLoadIds.includes(bt.id)
    );

    const nodeTrussStore = useTrussStore.getState();
    const uuidToPresetName = new Map<string, string>();

    nodeTrussStore.nodeTrusses.forEach((truss) => {
      const presetName = truss.presetName ?? truss.label;
      if (presetName) {
        uuidToPresetName.set(truss.id, presetName);
      }
    });

    // Map group.nodeLoadIds from UUIDs to preset names
    const presetNamesInGroup = new Set<string>();
    group.nodeLoadIds.forEach((id: string) => {
      const presetName = uuidToPresetName.get(id);
      if (presetName) {
        presetNamesInGroup.add(presetName);
      }
    });

    // Filter grouped node trusses by preset name
    const groupNodeLoads = groupedNodeTrusses.filter((nt: any) =>
      presetNamesInGroup.has(nt.name || nt.id)
    );

    lines.push(`${group.name} Start`);

    // Beam loads section
    lines.push(`BEAM LOADCASE START (FORMAT: ID TYPE VALUES BEAMIDS)`);

    groupBeamLoads.forEach((t, idx) => {
      let type = "";
      let values: string[] = [];

      switch (t.type) {
        case "distributed_uniform":
          type = "DISTRIBUTED1";
          values.push(`DX:${t.dx || 0}`, `DY:${t.dy || 0}`, `DZ:${t.dz || 0}`);
          break;
        case "distributed_variable":
          type = "DISTRIBUTED2";
          values.push(
            `DX_L:${t.dxLeft || 0}`,
            `DY_L:${t.dyLeft || 0}`,
            `DZ_L:${t.dzLeft || 0}`,
            `DX_R:${t.dxRight || 0}`,
            `DY_R:${t.dyRight || 0}`,
            `DZ_R:${t.dzRight || 0}`
          );
          break;
        case "concentrated_load":
          type = "CONCENTRATED";
          let refPoint = "S_Node";
          if (t.percentageFromNode === 50) {
            refPoint = "Center";
          } else if (t.referenceNode === "node2") {
            refPoint = "E_Node";
          }
          values.push(
            `FX:${t.fx || 0}`,
            `FY:${t.fy || 0}`,
            `FZ:${t.fz || 0}`,
            `REF:${refPoint}`,
            `DIST:${t.percentageFromNode || 0}`
          );
          break;
        case "twisted_load":
          type = "TWISTED";
          values.push(
            `TORQUE:${t.torque}`,
            `DIR:${t.torqueDirection?.toUpperCase()}`
          );
          break;
      }

      const beamIds = t.beamIds.length > 0 ? t.beamIds.join(",") : "";
      lines.push(`${idx + 1} ${type} ${values.join(" ")} ${beamIds};`);
    });

    lines.push(`BEAM LOADCASE END`);
    lines.push(``);

    // Node loads section
    lines.push(`NODE LOADCASE START (FORMAT: ID VALUES NODEIDS)`);

    groupNodeLoads.forEach((t: any, idx: number) => {
      const values: string[] = [];

      if (t.type === "regular") {
        values.push(
          `RX_X:${t.rotationValues.X}`,
          `RX_Y:${t.rotationValues.Y}`,
          `RX_Z:${t.rotationValues.Z}`
        );
      } else {
        values.push(
          `FX_X:${t.perpendicularValues.X}`,
          `FX_Y:${t.perpendicularValues.Y}`,
          `FX_Z:${t.perpendicularValues.Z}`
        );
      }

      const nodeIds = t.nodeIds.length ? t.nodeIds.join(",") : "";
      lines.push(`${idx + 1} ${values.join(" ")} ${nodeIds};`);
    });

    lines.push(`NODE LOADCASE END`);
    lines.push(`${group.name} END`);
    lines.push(``);
  });

  return lines.join("\n");
};

/**
 * Export model data to terminal text format
 */
export function exportToTerminalFormat(): string {
  const nodes = useModelStore.getState().nodes;
  const beams = useModelStore.getState().beams;
  const supports = useSupportStore.getState().supports;
  const properties = usePropertyStore.getState().properties;
  const beamTrusses = useBeamTrussStore.getState().beamTrusses;
  const nodeTrussesArray = useTrussStore.getState().nodeTrusses;
  const loadcaseGroups = useLoadcaseGroupStore.getState().loadcaseGroups;

  console.log("📤 [Export] nodeTrussesArray:", nodeTrussesArray);
  console.log("📤 [Export] loadcaseGroups:", loadcaseGroups);

  const nodeLines = formatNodesInRows(nodes);
  const beamLines = formatBeamsInRows(beams);
  const supportLines = formatSupportsInRows(supports);
  const propertyLines = formatPropertiesInRows(properties);

  const groupedNodeTrusses = groupNodeTrusses(nodeTrussesArray);

  console.log("📤 [Export] groupedNodeTrusses:", groupedNodeTrusses);

  const loadcaseGroupLines = formatLoadcaseGroups(
    loadcaseGroups,
    beamTrusses,
    groupedNodeTrusses
  );

  const template = `NODES START(FORMAT: NODEID X Y Z)
${nodeLines}
NODES END

BEAMS START(FORMAT: BEAMID STARTNODEID ENDNODEID)
${beamLines}
BEAMS END

SUPPORTS START(FORMAT: ID POSITION TYPE DOF(Fx,Fy,Fz,Rx,Ry,Rz) NODEID)
${supportLines}
SUPPORTS END

PROPERTIES START(FORMAT: ID MATERIAL SECTIONTYPE DESIGNATION BEAMIDS)
${propertyLines}
PROPERTIES END

${loadcaseGroupLines}`;

  console.log("📝 [TerminalFormatter] Generated terminal format");
  return template;
}

// ==================== IMPORT FROM TERMINAL FORMAT ====================

interface ParsedNode {
  id: number;
  x: number;
  y: number;
  z: number;
}

interface ParsedBeam {
  id: string;
  startNodeId: number;
  endNodeId: number;
  type: "beam";
}

/**
 * Parse terminal format text and import into stores
 */
export function importFromTerminalFormat(text: string): {
  success: boolean;
  errors: string[];
} {
  const lines = text.split("\n").map((l) => l.trim());

  let nodeSection = false;
  let beamSection = false;
  let supportSection = false;
  let propertySection = false;
  let nodeLoadcaseSection = false;
  let beamLoadcaseSection = false;

  let currentLoadcaseGroupName: string | null = null;
  let currentLoadcaseBeamLoads: any[] = [];
  let currentLoadcaseNodeLoads: any[] = [];

  const parsedNodes: ParsedNode[] = [];
  const parsedBeams: ParsedBeam[] = [];
  const parsedSupports: any[] = [];
  const parsedProperties: any[] = [];
  const parsedNodeLoadcases: any[] = [];
  const parsedBeamLoadcases: any[] = [];
  const parsedLoadcaseGroups: any[] = [];

  const errors: string[] = [];

  for (const line of lines) {
    if (!line || line.startsWith("CMD")) continue;

    // === Detect sections ===
    if (line.includes("NODES START")) {
      nodeSection = true;
      beamSection = false;
      supportSection = false;
      propertySection = false;
      continue;
    }
    if (line.includes("NODES END")) {
      nodeSection = false;
      continue;
    }

    if (line.includes("BEAMS START")) {
      beamSection = true;
      nodeSection = false;
      supportSection = false;
      propertySection = false;
      continue;
    }
    if (line.includes("BEAMS END")) {
      beamSection = false;
      continue;
    }

    if (line.includes("SUPPORTS START")) {
      supportSection = true;
      nodeSection = false;
      beamSection = false;
      propertySection = false;
      continue;
    }
    if (line.includes("SUPPORTS END")) {
      supportSection = false;
      continue;
    }

    if (line.includes("PROPERTIES START")) {
      propertySection = true;
      nodeSection = beamSection = supportSection = false;
      continue;
    }
    if (line.includes("PROPERTIES END")) {
      propertySection = false;
      continue;
    }

    // === NESTED LOADCASE GROUP DETECTION ===
    if (
      line.endsWith(" Start") &&
      !line.includes("LOADCASE START") &&
      !line.includes("NODES START") &&
      !line.includes("BEAMS START") &&
      !line.includes("SUPPORTS START") &&
      !line.includes("PROPERTIES START")
    ) {
      currentLoadcaseGroupName = line.replace(" Start", "");
      currentLoadcaseBeamLoads = [];
      currentLoadcaseNodeLoads = [];
      beamLoadcaseSection = false;
      nodeLoadcaseSection = false;
      continue;
    }

    if (
      line.endsWith(" END") &&
      !line.includes("LOADCASE END") &&
      !line.includes("NODES END") &&
      !line.includes("BEAMS END") &&
      !line.includes("SUPPORTS END") &&
      !line.includes("PROPERTIES END")
    ) {
      const groupName = line.replace(" END", "");
      if (currentLoadcaseGroupName === groupName) {
        const beamLoadIdMap = new Map<string, string>();
        const nodeLoadIdMap = new Map<string, string>();

        currentLoadcaseBeamLoads.forEach((bl, idx) => {
          const uniqueId = `beam-load-${parsedBeamLoadcases.length + idx + 1}`;
          beamLoadIdMap.set(bl.id, uniqueId);
          bl.id = uniqueId;
        });

        currentLoadcaseNodeLoads.forEach((nl, idx) => {
          const uniqueId = `node-load-${parsedNodeLoadcases.length + idx + 1}`;
          nodeLoadIdMap.set(nl.id, uniqueId);
          nl.id = uniqueId;
        });

        parsedLoadcaseGroups.push({
          id: `loadcase-${parsedLoadcaseGroups.length + 1}`,
          name: currentLoadcaseGroupName,
          beamLoadIds: currentLoadcaseBeamLoads.map((bl) => bl.id),
          nodeLoadIds: currentLoadcaseNodeLoads.map((nl) => nl.id),
        });

        currentLoadcaseBeamLoads.forEach((bl) => parsedBeamLoadcases.push(bl));
        currentLoadcaseNodeLoads.forEach((nl) => parsedNodeLoadcases.push(nl));

        currentLoadcaseGroupName = null;
        currentLoadcaseBeamLoads = [];
        currentLoadcaseNodeLoads = [];
        beamLoadcaseSection = false;
        nodeLoadcaseSection = false;
      }
      continue;
    }

    if (line.includes("NODE LOADCASE START")) {
      nodeLoadcaseSection = true;
      continue;
    }
    if (line.includes("NODE LOADCASE END")) {
      nodeLoadcaseSection = false;
      continue;
    }

    if (line.includes("BEAM LOADCASE START")) {
      beamLoadcaseSection = true;
      continue;
    }
    if (line.includes("BEAM LOADCASE END")) {
      beamLoadcaseSection = false;
      continue;
    }

    // ===================== NODES ====================
    if (nodeSection) {
      const parts = line
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean);

      parts.forEach((p) => {
        const vals = p.split(/\s+/);
        if (vals.length < 3 || vals.length > 4) {
          errors.push(`Node format error: "${p}"`);
          return;
        }
        const offset = vals.length === 4 ? 1 : 0;
        parsedNodes.push({
          id: parsedNodes.length + 1,
          x: Number(vals[offset]),
          y: Number(vals[offset + 1]),
          z: Number(vals[offset + 2]),
        });
      });

      continue;
    }

    // ===================== BEAMS ====================
    if (beamSection) {
      const parts = line
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean);

      parts.forEach((p) => {
        const vals = p.split(/\s+/);
        if (vals.length < 3 || vals.length > 4) {
          errors.push(`Beam format error: "${p}"`);
          return;
        }

        const offset = vals.length === 3 ? 1 : 0;

        parsedBeams.push({
          id: String(parsedBeams.length + 1),
          startNodeId: Number(vals[offset]),
          endNodeId: Number(vals[offset + 1]),
          type: "beam",
        });
      });

      continue;
    }

    // ===================== SUPPORTS ====================
    if (supportSection) {
      const parts = line
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean);

      parts.forEach((p) => {
        const dofMatch = p.match(/DOF\((.*?)\)/i);
        const dofValues = dofMatch ? dofMatch[1].split(",").map(Number) : null;

        let tokens = p
          .replace(/DOF\((.*?)\)/i, "")
          .trim()
          .split(/\s+/);

        const hasId = /^\d+$/.test(tokens[0]);
        const base = hasId ? 1 : 0;

        const rawPosition = tokens[base].toUpperCase();
        const position =
          AXIS_TO_SUPPORT_POSITION[rawPosition] ?? rawPosition.toLowerCase();

        const type = tokens[base + 1]?.toLowerCase();

        if (!AXIS_TO_SUPPORT_POSITION[rawPosition]) {
          errors.push(
            `Invalid support position: "${tokens[base]}" (must be +X, -X, +Y, -Y, +Z, or -Z)`
          );
          return;
        }

        if (!type) {
          errors.push(`Missing support type in: "${p}"`);
          return;
        }

        if (!VALID_SUPPORT_TYPES.includes(type)) {
          errors.push(
            `Invalid support type: "${tokens[base + 1]}" (must be PINNED, ROLLER or FIXED)`
          );
          return;
        }

        let nodeIds: number[] = [];
        const last = tokens[tokens.length - 1];
        if (/^[\d,]+$/.test(last)) {
          nodeIds = last.split(",").map(Number);
        }

        const supportObj: any = {
          nodeIds,
          position,
          type: type.toLowerCase(),
        };

        if (type.toLowerCase() === "fixed" && dofValues) {
          if (dofValues.length !== 6) {
            errors.push(`Invalid DOF values: "${p}"`);
            return;
          }

          supportObj.dof = {
            Fx: dofValues[0],
            Fy: dofValues[1],
            Fz: dofValues[2],
            Rx: dofValues[3],
            Ry: dofValues[4],
            Rz: dofValues[5],
          };
        }

        parsedSupports.push(supportObj);
      });

      continue;
    }

    // ===================== PROPERTIES ====================
    if (propertySection) {
      const parts = line
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean);

      parts.forEach((p) => {
        const tokens = p.split(/\s+/);

        if (tokens.length < 4) {
          errors.push(`Property format invalid: "${p}"`);
          return;
        }

        let cursor = 0;

        const P_ID = tokens[cursor++];

        const material: MaterialType =
          tokens[cursor] === "Steel" ? "Steel" : "None";
        cursor++;

        let sectionType: SectionType | undefined;
        let sectionTypeTokenCount = 0;

        for (const type of SECTION_TYPES) {
          const typeTokens = type.split(" ");
          const candidate = tokens
            .slice(cursor, cursor + typeTokens.length)
            .join(" ");

          if (candidate === type) {
            sectionType = type;
            sectionTypeTokenCount = typeTokens.length;
            break;
          }
        }

        if (!sectionType) {
          errors.push(`Unknown section type in "${p}"`);
          return;
        }

        cursor += sectionTypeTokenCount;

        let beamIds: string[] = [];
        let designationTokens = tokens.slice(cursor);

        let sectionData = null;

        while (designationTokens.length > 0) {
          const designationAttempt = designationTokens.join(" ");

          sectionData = getDesignationDataForType(
            sectionType,
            designationAttempt
          );

          if (sectionData) {
            break;
          }

          const token = designationTokens.pop()!;

          token.split(",").forEach((id) => {
            if (id.trim()) beamIds.unshift(id.trim());
          });
        }

        if (!sectionData) {
          errors.push(
            `Unknown section: ${sectionType} ${tokens.slice(cursor).join(" ")}`
          );
          return;
        }

        const designation = designationTokens.join(" ");

        parsedProperties.push({
          P_ID,
          beamIds,
          material,
          sectionType,
          sectionProperties: {
            ...sectionData,
            designation,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      });

      continue;
    }

    // ===================== NODE LOADCASES ====================
    if (nodeLoadcaseSection) {
      let tokens = line.replace(";", "").split(/\s+/);

      const id = tokens.shift()!;

      let nodeIds: number[] = [];

      const last = tokens[tokens.length - 1];
      if (/^[\d,]+$/.test(last)) {
        nodeIds = last.split(",").map(Number);
        tokens.pop();
      }

      const values: Record<string, number> = {};
      const invalidKeys: string[] = [];

      for (const kv of tokens) {
        if (!kv.trim()) continue;
        const colonIndex = kv.indexOf(":");
        if (colonIndex === -1) continue;
        const k = kv.slice(0, colonIndex).trim();
        const v = kv.slice(colonIndex + 1).trim();
        if (!k || !v) continue;
        if (!VALID_NODE_LOADCASE_KEYS.includes(k)) {
          invalidKeys.push(k);
          continue;
        }

        values[k] = Number(v);
      }

      if (invalidKeys.length > 0) {
        errors.push(
          `Invalid node loadcase keys: ${invalidKeys.join(", ")} in "${line}"`
        );
        continue;
      }

      if (currentLoadcaseGroupName) {
        currentLoadcaseNodeLoads.push({
          id,
          targetType: "NODE",
          targetIds: nodeIds,
          name: id,
          values,
        });
      } else {
        parsedNodeLoadcases.push({
          id,
          targetType: "NODE",
          targetIds: nodeIds,
          name: id,
          values,
        });
      }

      continue;
    }

    // ===================== BEAM LOADCASES ====================
    if (beamLoadcaseSection) {
      let tokens = line.replace(";", "").split(/\s+/);

      const id = tokens.shift()!;
      const type = tokens.shift()!;

      if (!VALID_BEAM_LOADCASE_TYPES.includes(type)) {
        errors.push(
          `Invalid beam loadcase type: "${type}" (must be DISTRIBUTED1, DISTRIBUTED2, CONCENTRATED, or TWISTED)`
        );
        continue;
      }

      let beamIds: number[] = [];
      const last = tokens[tokens.length - 1];
      if (/^[\d,]+$/.test(last)) {
        beamIds = last.split(",").map(Number);
        tokens.pop();
      }

      const values: Record<string, any> = {};

      for (const kv of tokens) {
        if (!kv.trim()) continue;
        const colonIndex = kv.indexOf(":");
        if (colonIndex === -1) continue;
        const k = kv.slice(0, colonIndex).trim();
        const v = kv.slice(colonIndex + 1).trim();
        if (!k || !v) continue;

        if (!isNaN(Number(v))) {
          values[k] = Number(v);
        } else {
          values[k] = v;
        }
      }

      if (currentLoadcaseGroupName) {
        currentLoadcaseBeamLoads.push({
          id,
          type,
          targetType: "BEAM",
          targetIds: beamIds,
          name: id,
          values,
        });
      } else {
        parsedBeamLoadcases.push({
          id,
          type,
          targetType: "BEAM",
          targetIds: beamIds,
          name: id,
          values,
        });
      }

      continue;
    }
  }

  // Now apply the parsed data to stores
  console.log("📥 [TerminalFormatter] Applying parsed data to stores...");

  // Clear existing data (don't clear here - already done in importAllData)
  // useModelStore.setState({ nodes: [], beams: [] });
  // useSupportStore.getState().clearAllSupports();
  // usePropertyStore.setState({ properties: [] });
  // useBeamTrussStore.setState({ beamTrusses: [] });
  // useTrussStore.setState({ nodeTrusses: [] });
  // useLoadcaseGroupStore.setState({ loadcaseGroups: [] });

  // Apply nodes
  if (parsedNodes.length > 0) {
    useModelStore.getState().setNodes(parsedNodes as any);
    console.log("  ✅ Applied nodes:", parsedNodes.length);
  }

  // Apply beams
  if (parsedBeams.length > 0) {
    useModelStore.getState().setBeams(parsedBeams as any);
    console.log("  ✅ Applied beams:", parsedBeams.length);
  }

  // Apply supports
  if (parsedSupports.length > 0) {
    parsedSupports.forEach((s) => {
      if (s.nodeIds && s.nodeIds.length > 0) {
        s.nodeIds.forEach((nodeId: number) => {
          useSupportStore
            .getState()
            .addSupport([String(nodeId)], s.position, s.type);
        });
      }
    });
    console.log("  ✅ Applied supports:", parsedSupports.length);
  }

  // Apply properties
  if (parsedProperties.length > 0) {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#FFA07A",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E2",
      "#F8B739",
      "#52C77E",
    ];

    const propertiesWithMetadata = parsedProperties.map((prop, idx) => ({
      ...prop,
      id: `prop-${Date.now()}-${idx}`,
      color: colors[idx % colors.length],
      createdAt: prop.createdAt || new Date().toISOString(),
      updatedAt: prop.updatedAt || new Date().toISOString(),
    }));

    // Set properties first
    usePropertyStore.setState({
      properties: propertiesWithMetadata,
    });

    // Wait for all stores to sync, then apply beam assignments
    setTimeout(() => {
      const propertyStore = usePropertyStore.getState();

      propertiesWithMetadata.forEach((prop) => {
        if (prop.beamIds && prop.beamIds.length > 0) {
          console.log(
            `🔗 Applying property ${prop.P_ID} to beams:`,
            prop.beamIds
          );
          propertyStore.applyPropertyToBeams(prop.P_ID, prop.beamIds);
        }
      });

      console.log("  ✅ Applied property beam assignments");
    }, 200); // Increased delay for store sync

    console.log("  ✅ Applied properties:", parsedProperties.length);
  }

  // ✅ STEP 1: Create loadcase groups (metadata only)
  if (parsedLoadcaseGroups.length > 0) {
    const finalGroups = parsedLoadcaseGroups.map((g) => ({
      ...g,
      createdAt: Date.now(),
      beamLoadIds: g.beamLoadIds,
      nodeLoadIds: g.nodeLoadIds,
    }));

    useLoadcaseGroupStore.setState({ loadcaseGroups: finalGroups });

    if (finalGroups.length > 0) {
      useLoadcaseGroupStore.getState().setActiveLoadcase(finalGroups[0].name);
      console.log(`  📍 Set active loadcase: ${finalGroups[0].name}`);
    }

    console.log("  ✅ Applied loadcase groups:", parsedLoadcaseGroups.length);
  }

  // ✅ STEP 2: Apply ALL node loadcases
  const allNodeLoadcases = [...parsedNodeLoadcases];
  const parsedToActualNodeLoadId = new Map<string, string>();

  if (allNodeLoadcases.length > 0) {
    allNodeLoadcases.forEach((lc) => {
      if (lc.targetType !== "NODE") return;
      const trussStore = useTrussStore.getState();

      if (typeof trussStore.removeTrussesByLabel === "function") {
        trussStore.removeTrussesByLabel(lc.name);
      }

      const rotationValues = {
        X: lc.values.RX_X ?? 0,
        Y: lc.values.RX_Y ?? 0,
        Z: lc.values.RX_Z ?? 0,
      };
      const perpendicularValues = {
        X: lc.values.FX_X ?? 0,
        Y: lc.values.FX_Y ?? 0,
        Z: lc.values.FX_Z ?? 0,
      };
      const targetNodeIds = lc.targetIds.map(String);

      if (typeof trussStore.setSelectedPresetName === "function") {
        trussStore.setSelectedPresetName(lc.name);
      }

      const beforeTrusses = trussStore.nodeTrusses.filter(
        (t) => t.presetName === lc.name || t.label === lc.name
      );
      const beforeIds = new Set(beforeTrusses.map((t) => t.id));

      if (targetNodeIds.length === 0) {
        (["X", "Y", "Z"] as const).forEach((axis) => {
          if (rotationValues[axis]) {
            trussStore.addTruss(
              [],
              "regular",
              axis,
              undefined,
              undefined,
              rotationValues[axis],
              lc.name
            );
          }
          if (perpendicularValues[axis]) {
            trussStore.addTruss(
              [],
              "perpendicular",
              axis,
              undefined,
              undefined,
              perpendicularValues[axis],
              lc.name
            );
          }
        });

        if (typeof trussStore.setSelectedPresetName === "function") {
          trussStore.setSelectedPresetName(null);
        }

        const afterTrusses = trussStore.nodeTrusses.filter(
          (t) => t.presetName === lc.name || t.label === lc.name
        );

        afterTrusses.forEach((truss) => {
          trussStore.updateTruss(truss.id, { visible: true });
        });

        const newIds = afterTrusses
          .filter((t) => !beforeIds.has(t.id))
          .map((t) => t.id);

        // Store the load name for this group
        parsedToActualNodeLoadId.set(lc.id, lc.name);
        return;
      }

      console.log(
        `🔵 Applying node load "${lc.name}" to nodes:`,
        targetNodeIds,
        "RX:",
        rotationValues,
        "FX:",
        perpendicularValues
      );

      if (typeof trussStore.applyLoadOnNode === "function") {
        trussStore.applyLoadOnNode(
          targetNodeIds,
          rotationValues,
          perpendicularValues
        );
      }

      if (typeof trussStore.setSelectedPresetName === "function") {
        trussStore.setSelectedPresetName(null);
      }

      const afterTrusses = trussStore.nodeTrusses.filter(
        (t) => t.presetName === lc.name || t.label === lc.name
      );

      afterTrusses.forEach((truss) => {
        trussStore.updateTruss(truss.id, { visible: true });
      });

      const newIds = afterTrusses
        .filter((t) => !beforeIds.has(t.id))
        .map((t) => t.id);

      parsedToActualNodeLoadId.set(lc.id, lc.name);
    });
    console.log("  ✅ Applied node loadcases:", allNodeLoadcases.length);
  }

  // ✅ STEP 3: Track mapping from parsed IDs to actual store IDs for beam loads
  const parsedToActualBeamLoadId = new Map<string, string>();

  // ✅ STEP 4: Apply beam loadcases
  if (parsedBeamLoadcases.length > 0) {
    parsedBeamLoadcases
      .filter((lc) => lc.targetType === "BEAM")
      .forEach((lc) => {
        const store = useBeamTrussStore.getState();
        if (!lc.type) {
          console.warn(`Beam loadcase ${lc.id} missing TYPE`);
          return;
        }

        const trussType =
          lc.type === "DISTRIBUTED1"
            ? "distributed_uniform"
            : lc.type === "DISTRIBUTED2"
              ? "distributed_variable"
              : lc.type === "CONCENTRATED"
                ? "concentrated_load"
                : lc.type === "TWISTED"
                  ? "twisted_load"
                  : null;

        if (!trussType) {
          console.warn(`Invalid beam loadcase type: ${lc.type}`);
          return;
        }

        // Check if load already exists by label
        const existing = store.beamTrusses.find((t) => t.label === lc.id);

        if (existing) {
          // Update existing load
          store.updateBeamTruss(existing.id, {
            type: trussType as BeamTrussType,
            dx: lc.values.DX ?? 0,
            dy: lc.values.DY ?? 0,
            dz: lc.values.DZ ?? 0,
            dxLeft: lc.values.DX_L ?? 0,
            dyLeft: lc.values.DY_L ?? 0,
            dzLeft: lc.values.DZ_L ?? 0,
            dxRight: lc.values.DX_R ?? 0,
            dyRight: lc.values.DY_R ?? 0,
            dzRight: lc.values.DZ_R ?? 0,
            fx: lc.values.FX ?? 0,
            fy: lc.values.FY ?? 0,
            fz: lc.values.FZ ?? 0,
            referenceNode: (lc.values.REF === "Center"
              ? "node1"
              : lc.values.REF === "E_Node"
                ? "node2"
                : "node1") as "node1" | "node2",
            percentageFromNode:
              lc.values.REF === "Center" ? 50 : (lc.values.DIST ?? 0),
            torque: lc.values.TORQUE ?? 0,
            torqueDirection: (lc.values.DIR?.toLowerCase() ?? "clockwise") as
              | "clockwise"
              | "anticlockwise",
            beamIds: lc.targetIds.map(String),
            visible: true,
          });
          parsedToActualBeamLoadId.set(lc.id, existing.id);
          console.log(`✅ Updated beam load: ${lc.id} (${trussType})`);
        } else {
          // Create new load
          const newId = store.addBeamTruss(
            lc.targetIds.map(String),
            trussType,
            lc.id,
            lc.values.DX ?? 0,
            lc.values.DY ?? 0,
            lc.values.DZ ?? 0,
            lc.values.DX_R ?? 0,
            lc.values.DY_R ?? 0,
            lc.values.DZ_R ?? 0,
            lc.values.FX ?? 0,
            lc.values.FY ?? 0,
            lc.values.FZ ?? 0,
            (lc.values.REF === "Center"
              ? "node1"
              : lc.values.REF === "E_Node"
                ? "node2"
                : "node1") as "node1" | "node2",
            lc.values.REF === "Center" ? 50 : (lc.values.DIST ?? 0),
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            lc.values.TORQUE ?? 0,
            (lc.values.DIR?.toLowerCase() ?? "clockwise") as
              | "clockwise"
              | "anticlockwise"
          );

          if (newId) {
            store.updateBeamTruss(newId, {
              type: trussType as BeamTrussType,
              label: lc.id,
              dx: lc.values.DX ?? 0,
              dy: lc.values.DY ?? 0,
              dz: lc.values.DZ ?? 0,
              dxLeft: lc.values.DX_L ?? 0,
              dyLeft: lc.values.DY_L ?? 0,
              dzLeft: lc.values.DZ_L ?? 0,
              dxRight: lc.values.DX_R ?? 0,
              dyRight: lc.values.DY_R ?? 0,
              dzRight: lc.values.DZ_R ?? 0,
              fx: lc.values.FX ?? 0,
              fy: lc.values.FY ?? 0,
              fz: lc.values.FZ ?? 0,
              referenceNode: (lc.values.REF === "Center"
                ? "node1"
                : lc.values.REF === "E_Node"
                  ? "node2"
                  : "node1") as "node1" | "node2",
              percentageFromNode:
                lc.values.REF === "Center" ? 50 : (lc.values.DIST ?? 0),
              torque: lc.values.TORQUE ?? 0,
              torqueDirection: (lc.values.DIR?.toLowerCase() ?? "clockwise") as
                | "clockwise"
                | "anticlockwise",
              beamIds: lc.targetIds.map(String),
              visible: true,
            });

            parsedToActualBeamLoadId.set(lc.id, newId);
            console.log(
              `✅ Created beam load: ${lc.id} (${trussType}) on beams:`,
              lc.targetIds
            );
          }
        }
      });
    console.log("  ✅ Applied beam loadcases:", parsedBeamLoadcases.length);
  }

  // ✅ STEP 5: Update loadcase groups with actual store IDs
  if (parsedLoadcaseGroups.length > 0) {
    const currentGroups = useLoadcaseGroupStore.getState().loadcaseGroups;

    const updatedGroups = currentGroups.map((g) => {
      // Map node load IDs from parsed IDs to actual preset names
      const mappedNodeLoadIds = g.nodeLoadIds
        .map((id: string) => parsedToActualNodeLoadId.get(id) || id)
        .filter(Boolean);

      return {
        ...g,
        beamLoadIds: g.beamLoadIds
          .map((id: string) => parsedToActualBeamLoadId.get(id) || id)
          .filter(Boolean),
        nodeLoadIds: mappedNodeLoadIds,
      };
    });

    useLoadcaseGroupStore.setState({ loadcaseGroups: updatedGroups });
    console.log("  ✅ Updated loadcase group IDs (beam + node)");
    console.log("  🔍 Updated groups:", updatedGroups);
  }

  // ✅ STEP 6: Dispatch UI update events
  setTimeout(() => {
    // Force refresh of truss store by creating new array references
    const currentNodeTrusses = useTrussStore.getState().nodeTrusses;
    useTrussStore.setState({
      nodeTrusses: [...currentNodeTrusses], // Create new array to trigger subscribers
    });

    console.log(
      "  🔄 Forced truss store update, current trusses:",
      currentNodeTrusses.length
    );

    // Dispatch all update events
    window.dispatchEvent(new CustomEvent("beams-updated"));
    window.dispatchEvent(new CustomEvent("nodes-updated"));
    window.dispatchEvent(new CustomEvent("supports-updated"));
    window.dispatchEvent(new CustomEvent("properties-updated"));
    window.dispatchEvent(new CustomEvent("loads-updated"));
    window.dispatchEvent(new CustomEvent("predefined_truss_presets_updated"));

    console.log("  📢 Dispatched UI update events");
  }, 300); // Increased from 100 to 300ms

  console.log("✅ [TerminalFormatter] Import complete");

  setTimeout(() => {
    const trussStore = useTrussStore.getState();
    const allTrusses = trussStore.nodeTrusses;

    console.log("🔍 [DEBUG] All node trusses:", allTrusses);
    console.log("🔍 [DEBUG] Truss count:", allTrusses.length);

    allTrusses.forEach((truss, idx) => {
      console.log(`🔍 [DEBUG] Truss ${idx + 1}:`, {
        id: truss.id,
        label: truss.label,
        presetName: truss.presetName,
        visible: truss.visible,
        type: truss.type,
        plane: truss.plane,
        nodeIds: truss.nodeIds,
      });
    });

    // Now FORCE them all to visible
    console.log("🔧 [FIX] Setting all trusses to visible...");
    allTrusses.forEach((truss) => {
      trussStore.updateTruss(truss.id, { visible: true });
    });

    // Force UI refresh
    useTrussStore.setState({ nodeTrusses: [...trussStore.nodeTrusses] });
    window.dispatchEvent(new CustomEvent("loads-updated"));
  }, 500);

  setTimeout(() => {
    const trussStore = useTrussStore.getState();
    const loadcaseStore = useLoadcaseGroupStore.getState();

    // Get all node trusses that were just imported
    const allTrusses = trussStore.nodeTrusses;

    const activeLoadcase = loadcaseStore.loadcaseGroups.find(
      (g: any) => g.isActive || g.active
    );
    console.log("   Active loadcase:", activeLoadcase?.name || "None");

    // Force all imported trusses to visible=true
    allTrusses.forEach((truss) => {
      if (!truss.visible) {
        trussStore.updateTruss(truss.id, { visible: true });
      }
    });

    // Force store update with new array reference
    useTrussStore.setState({
      nodeTrusses: [...trussStore.nodeTrusses],
    });

    // Dispatch comprehensive UI update events
    window.dispatchEvent(new CustomEvent("nodes-updated"));
    window.dispatchEvent(new CustomEvent("loads-updated"));
    window.dispatchEvent(new CustomEvent("predefined_truss_presets_updated"));
    window.dispatchEvent(new CustomEvent("truss-visibility-changed"));
  }, 600);

  return {
    success: errors.length === 0,
    errors,
  };
}
